import React from "react";
import i18n from "../lib/i18n";
import PropTypes from "prop-types";

import PaginationRow from "./PaginationRow";
import LoadWrapper from "./LoadWrapper";
import ListSettings from "./ListSettings";
import ListHeaderCell from "./ListHeaderCell";
import ListContainer from "./ListContainer";

import { IntercomLink } from "@/javascript/react/components/intercom_link.component";
import { currency } from "../lib/helpers";

const SuggestionList = (props) => {
  let headers = [
    {
      field: "name",
      title: i18n.t("js.components.suggestion_list.name"),
      width: 200,
    },
    {
      field: "unit_price",
      title: i18n.t("js.components.suggestion_list.unit_price"),
      className: "right-align",
    },
    { field: "unit", title: i18n.t("js.components.suggestion_list.unit") },
    {
      field: "repeated",
      title: i18n.t("js.components.suggestion_list.repeated"),
    },
    ...(props.sustainabilityEnabled
      ? [
          {
            field: "carbon_dioxide_equivalent",
            title: i18n.t("js.components.suggestion_list.emission"),
          },
          {
            field: "is_energy_saving",
            title: i18n.t("js.components.suggestion_list.energy_saving"),
          },
        ]
      : []),
  ];

  return (
    <React.Fragment>
      <ListSettings
        filter={props.filter}
        onFilterChange={(event) => props.onFilterChange(event.target.value)}
        onPerPageChange={(event) =>
          props.onPerPageChange(parseInt(event.target.value, 10))
        }
        perPage={props.perPage}
        ariaControls="suggestion-list"
      />

      <LoadWrapper isLoading={props.isLoading} key={props.isLoading ? 1 : 0}>
        <table
          className="table table-hover table-suggestions pl-data-table striped"
          id="suggestion-list"
        >
          <thead>
            <tr>
              <th style={{ width: "40px" }}></th>
              {headers.map((headerItem) => (
                <ListHeaderCell
                  key={headerItem.field}
                  {...headerItem}
                  sorting={props.sorting}
                  onSortChange={props.onSortChange}
                />
              ))}
            </tr>
          </thead>

          <tbody>
            {props.items.map((suggestion) => {
              if (
                suggestion.attributes.built_in &&
                props.hasMaskedSuggestions &&
                !suggestion.attributes.included_in_trial_accounts
              ) {
                return (
                  <MaskedRow
                    suggestion={suggestion}
                    sustainabilityEnabled={props.sustainabilityEnabled}
                    key={suggestion.id}
                  />
                );
              }

              return (
                <Row
                  suggestion={suggestion}
                  onSelect={props.onSelect}
                  sustainabilityEnabled={props.sustainabilityEnabled}
                  key={suggestion.id}
                />
              );
            })}
          </tbody>
        </table>
      </LoadWrapper>

      <PaginationRow
        page={props.page}
        itemsPerPage={props.perPage}
        totalNumberOfItems={props.total}
        setPage={props.onPageChange}
        ariaControls="suggestion-list"
      />

      <InfoArea hasMaskedSuggestions={props.hasMaskedSuggestions} />
    </React.Fragment>
  );
};

const MaskedRow = ({ suggestion, sustainabilityEnabled }) => {
  return (
    <tr
      className="non-clickable"
      style={{
        backgroundColor: "#f3f3f3",
      }}
    >
      <td style={{ verticalAlign: "middle" }}>
        {suggestion.attributes.built_in && <PlanimaLogo />}
      </td>
      <td style={{ color: "#666", verticalAlign: "middle" }}>
        {suggestion.attributes.name}
      </td>
      <td></td>
      <td></td>
      <td></td>
      {sustainabilityEnabled && (
        <>
          <td></td>
          <td></td>
        </>
      )}
    </tr>
  );
};

const Row = ({ suggestion, onSelect, sustainabilityEnabled }) => {
  return (
    <tr
      onClick={() => onSelect(suggestion)}
      onKeyPress={(e) => e.key == "Enter" && onSelect(suggestion)}
      className="clickable"
      tabIndex="0"
    >
      <td>{suggestion.attributes.built_in && <PlanimaLogo />}</td>
      <td>{suggestion.attributes.name}</td>
      <td className="right-align">
        {currency(suggestion.attributes.unit_price)}
      </td>
      <td>{suggestion.attributes.unit}</td>
      <td>
        {suggestion.attributes.repeated &&
          `${suggestion.attributes.repeated} ${i18n.t("js.helpers.year").toLowerCase()}`}
      </td>
      {sustainabilityEnabled && (
        <>
          <td>
            {suggestion.attributes.carbon_dioxide_equivalent &&
              `${suggestion.attributes.carbon_dioxide_equivalent} ${i18n.t("js.helpers.emission_unit")}`}
          </td>
          <td>
            {suggestion.attributes.is_energy_saving && (
              <i className="fa fa-leaf" style={{ color: "green" }} />
            )}
          </td>
        </>
      )}
    </tr>
  );
};

const InfoArea = ({ hasMaskedSuggestions }) => {
  if (hasMaskedSuggestions) {
    return (
      <div className="help-block well mb-0">
        <h5 className="mt-0">
          {i18n.t("js.components.suggestion_list.trial_account_info_title")}
        </h5>
        <p>
          {i18n.t("js.components.suggestion_list.trial_account_info_1")}{" "}
          <strong>
            {i18n.t("js.components.suggestion_list.built_in_suggestion")}
          </strong>
          {i18n.t("js.components.suggestion_list.trial_account_info_2")}
        </p>
        <p>
          {i18n.t("js.components.suggestion_list.show_more")}{" "}
          <IntercomLink command="show">
            {i18n.t("js.components.suggestion_list.contact_us")}
          </IntercomLink>
        </p>
      </div>
    );
  }

  return (
    <div className="help-block well mb-0">
      <h5 className="mt-0">
        {i18n.t("js.components.suggestion_list.suggestions_info_title")}
      </h5>
      <p>
        {i18n.t("js.components.suggestion_list.suggestion_info_1")}{" "}
        <strong>
          {i18n.t("js.components.suggestion_list.built_in_suggestion")}
        </strong>
        {i18n.t("js.components.suggestion_list.suggestion_info_2")}{" "}
        <a
          href="https://share.hsforms.com/1sTVJ1zgwRRGjWqC_Aieyvg4asjk"
          target="_blank"
          rel="noopener noreferrer"
        >
          {i18n.t("js.components.suggestion_list.feedback")}
        </a>
      </p>
      <p>
        {i18n.t("js.components.suggestion_list.suggestion_info_3")}{" "}
        <IntercomLink command="show">
          {i18n.t("js.components.suggestion_list.contact_us").toLowerCase()}
        </IntercomLink>
      </p>
    </div>
  );
};

const PlanimaLogo = () => (
  <div
    className="logo-element-bw"
    alt="Logga för Planima-priser"
    title={"Planima-pris"}
    data-cy="planima-logo"
  ></div>
);

SuggestionList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
      unit: PropTypes.string,
      unit_price: PropTypes.number,
      repeated: PropTypes.number,
      carbon_dioxide_equivalent: PropTypes.number,
      is_energy_saving: PropTypes.bool,
      built_in: PropTypes.bool,
      included_in_trial_accounts: PropTypes.bool,
    })
  ).isRequired,
  filter: PropTypes.string,
  page: PropTypes.number,
  perPage: PropTypes.number,
  total: PropTypes.number,
  onSortChange: PropTypes.func,
  onPerPageChange: PropTypes.func,
  onPageChange: PropTypes.func,
  onFilterChange: PropTypes.func,
  onSelect: PropTypes.func,
  sorting: PropTypes.shape({
    field: PropTypes.string,
    order: PropTypes.oneOf([ListContainer.ASC, ListContainer.DESC]),
  }),
  isLoading: PropTypes.bool,
  hasMaskedSuggestions: PropTypes.bool.isRequired,
  sustainabilityEnabled: PropTypes.bool,
};

export default SuggestionList;
